import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { deviceDetailsThrottler } from "@/utils/apiThrottling";
import { syncDeviceDetails } from "@/utils/deviceSync";
import { useVisibilityRefresh } from "./useVisibilityRefresh";

interface DeviceResponse {
  deviceName?: string;
  deviceLastUpdated?: string;
  notification?: any;
  imageKit?: any;
  contract?: {
    scheduleStart?: string;
    scheduleEnd?: string;
    interval?: number;
    enablePublic?: boolean;
    onDemand?: number;
    imagesAllowed?: number;
    imagesCurrent?: number;
    imagesOverflow?: number;
  };
  [key: string]: any;
}

// Normalize database data to match API structure
const normalizeDeviceData = (dbData: any): DeviceResponse => {
  if (!dbData) return dbData;

  // Only log in development mode
  if (import.meta.env.DEV) {
    console.log('[Dev] Normalizing DB data for:', dbData.device_id);
  }

  // Map snake_case database fields to camelCase API format
  const normalized: DeviceResponse = {
    deviceID: dbData.device_id,
    deviceName: dbData.device_name,
    deviceDescription: dbData.device_description,
    deviceLastUpdated: dbData.device_last_updated,
    deviceStatus: dbData.device_status,
    deviceFirmware: dbData.device_firmware,
    deviceSerial: dbData.device_serial,
    currentHdop: dbData.current_hdop,
    latWgs84: dbData.lat_wgs84,
    longWgs84: dbData.long_wgs84,
    groupName: dbData.group_name,
    groupReference: dbData.group_reference,
    
    // Handle contract data - map individual fields to contract object
    contract: {
      imagesAllowed: dbData.images_allowed,
      imagesCurrent: dbData.images_current,
      imagesOverflow: dbData.images_overflow,
      scheduleStart: dbData.notification?.scheduleStart,
      scheduleEnd: dbData.notification?.scheduleEnd,
      interval: dbData.notification?.interval,
      enablePublic: dbData.notification?.enablePublic,
      onDemand: dbData.notification?.onDemand,
    },

    // Keep JSONB fields as they are (already proper structure)
    notification: dbData.notification,
    imageKit: dbData.imagekit, // Note: database uses lowercase 'imagekit'
    cellular: dbData.cellular,
    battery: dbData.battery,
    gnss: dbData.gnss,
    location: dbData.location,
    safeSearch: dbData.safe_search,
    
    // Handle camera config
    camera_config: dbData.camera_config,
    
    // Handle AI model field
    notificationAiModel: dbData.notification_ai_model,
    
    // Handle past images array
    pastImages: dbData.past_images,
    
    // Handle boundary boxes for ROI
    boundary_boxes: dbData.boundary_boxes || [],
    
    // Notification data fields
    notification_data_tags: dbData.notification_data_tags,
    notification_data: dbData.notification_data,

    // Image privacy level (0=none, 1=blur, 2=mask)
    devicePrivacyLevel: dbData.device_privacy_level,

    // Operational days CSV (long weekday names)
    deviceOperationalDays: dbData.device_operational_days,

    // Event schedule (special-day overrides)
    deviceEventDates: dbData.device_event_dates,
    eventScheduleSleepTime: dbData.event_schedule_sleep_time,
    
    // Include any additional fields from database
    ...dbData
  };

  return normalized;
};

export const useRealtimeDeviceDetails = (deviceId: string | undefined) => {
  const queryClient = useQueryClient();
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Removed individual realtime subscriptions - now handled by centralized subscription

  // Main device details query - database first, API fallback
  const query = useQuery({
    queryKey: ['device-details', deviceId],
    queryFn: async () => {
      if (!deviceId) return null;

      try {
        // First, try to get data from database
        const { data: dbData } = await supabase
          .from('device_details')
          .select('*')
          .eq('device_id', deviceId)
          .maybeSingle();

        // Always use database data if available, regardless of age
        if (dbData) {
          // Normalize database data to API format before returning
          return normalizeDeviceData(dbData);
        }

        // Only fetch from API if no database data exists
        if (import.meta.env.DEV) {
          console.log('[Dev] No DB data, fetching from API:', deviceId);
        }

        // Resolve device ID using the database function
        const { data: resolvedId } = await supabase.rpc('resolve_device_id', {
          input_id: deviceId
        });

        const finalDeviceId = resolvedId || deviceId;

        // Make API call using throttler
        const apiResponse = await deviceDetailsThrottler.addRequest(finalDeviceId, async () => {
          const { data, error } = await supabase.functions.invoke('deviceDetails', {
            body: { deviceId: finalDeviceId }
          });
          if (error) {
            // Upstream may return no usable data; don't crash the page
            console.warn('deviceDetails unavailable for', finalDeviceId, error.message);
            return null;
          }
          return data;
        });


        // Process and sync the API response
        let processedData = apiResponse;
        if (apiResponse?.imageList && typeof apiResponse.imageList === 'string') {
          try {
            processedData = {
              ...apiResponse,
              imageList: JSON.parse(apiResponse.imageList)
            };
          } catch (e) {
            console.warn('Failed to parse imageList for device:', deviceId, e);
          }
        }

        if (!processedData) return null;

        // Sync to database
        await syncDeviceDetails(finalDeviceId, processedData);

        // Merge API data with database camera_config (database takes priority)
        if (dbData?.camera_config && processedData) {
          processedData.camera_config = dbData.camera_config;
        }

        return processedData;

      } catch (error) {
        console.error('Error fetching device details:', error);
        throw error;
      }
    },
    enabled: !!deviceId,
    staleTime: 60 * 1000, // 60 seconds - allow data to become stale
    refetchInterval: false, // Disable polling, rely on realtime updates
    refetchOnWindowFocus: true, // Enable refetch when tab becomes active
    refetchOnMount: true, // Refetch on component remount
  });

  const updateDevice = async (field: string, value: string | number) => {
    if (!deviceId) return;

    try {
      const { error } = await supabase.functions.invoke('updateDevice', {
        body: { deviceId, field, value }
      });

      if (error) throw error;

      // Sync updated data and invalidate queries
      await syncDeviceDetails(deviceId, { [field]: value });
      queryClient.invalidateQueries({ queryKey: ['device-details', deviceId] });
      
      console.log('Device updated successfully:', deviceId, field, value);
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  };

  const refreshDevice = async () => {
    console.log('Manual refresh triggered for device:', deviceId);
    queryClient.invalidateQueries({ queryKey: ['device-details', deviceId] });
  };

  const forceApiRefresh = async () => {
    if (!deviceId) return;
    
    console.log('Force API refresh triggered for device:', deviceId);
    
    // Note: Forced refresh bypasses normal throttling
    
    // Remove cache and refetch
    queryClient.removeQueries({ queryKey: ['device-details', deviceId] });
    await queryClient.refetchQueries({ queryKey: ['device-details', deviceId] });
  };

  return {
    device: query.data,
    isLoading: query.isLoading,
    error: query.error,
    lastUpdated,
    updateDevice,
    refreshDevice,
    forceApiRefresh,
  };
};